import React, { useState } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { Home, Search, PlusSquare, MessageCircle, User, ShoppingCart, X } from 'lucide-react';
import { useAuth } from '../../components/AuthContext';
import { useUnreadCount } from '../../lib/chat/hooks/useUnreadCount';
import { supabase } from '../../lib/db';
import { useLanguage } from '../../contexts/LanguageContext';
import { SellerProfileForm } from '../../components/SellerProfileForm';
import { GoogleMapsProvider } from '../../components/GoogleMapsProvider';
import toast from 'react-hot-toast';

interface FeedBottomNavProps {
  onSearchOpen: () => void;
}

export const FeedBottomNav: React.FC<FeedBottomNavProps> = ({ onSearchOpen }) => {
  const location = useLocation();
  const history = useHistory();
  const { user, updateUserData } = useAuth();
  const unreadCount = useUnreadCount(user?.id || '');
  const { t } = useLanguage();
  const [showSellerPrompt, setShowSellerPrompt] = useState(false);
  const [showSellerForm, setShowSellerForm] = useState(false);
  const [switchingRole, setSwitchingRole] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [pickupLocations, setPickupLocations] = useState<string[]>([]);

  const handleCreateClick = async () => {
    if (!user) {
      history.push('/login');
      return;
    }

    // If already a seller, go directly to add item
    if (user.role === 'seller') {
      history.push('/profile?action=add-item');
      return;
    }

    // Check if user has a seller profile
    try {
      setSwitchingRole(true);
      const { data: sellerProfile } = await supabase
        .from('seller_profiles')
        .select('id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

      if (sellerProfile) {
        // Has seller profile but current role is not seller — switch silently and go to add item
        await supabase
          .from('profiles')
          .update({ role: 'seller', updated_at: new Date().toISOString() })
          .eq('user_id', user.id);
        await updateUserData();
        history.push('/profile?action=add-item');
      } else {
        // No seller profile — prompt to create one
        setShowSellerPrompt(true);
      }
    } catch (error) {
      console.error('Error checking seller profile:', error);
    } finally {
      setSwitchingRole(false);
    }
  };

  const handleOpenSellerForm = () => {
    setShowSellerPrompt(false);
    setShowSellerForm(true);
  };

  const handleSellerProfileSubmit = async (data: any) => {
    try {
      const { error } = await supabase.from('seller_profiles').insert([
        {
          ...data,
          user_id: user!.id,
          created_at: new Date().toISOString(),
        },
      ]);
      if (error) throw error;

      // Switch role to seller
      await supabase
        .from('profiles')
        .update({ role: 'seller', updated_at: new Date().toISOString() })
        .eq('user_id', user!.id);
      await updateUserData();
      setShowSellerForm(false);
      toast.success(t('profileUpdateSuccess'));
      history.push('/profile?action=add-item');
    } catch (error) {
      console.error('Error creating seller profile:', error);
      toast.error(t('profileUpdateFailure'));
    }
  };

  const navItems = [
    { id: 'home', to: '/', icon: Home, label: 'Home' },
    { id: 'search', to: '#search', icon: Search, label: 'Discover' },
    { id: 'create', to: '/profile', icon: PlusSquare, label: '' },
    { id: 'chat', to: '/chat', icon: MessageCircle, label: 'Inbox' },
    { id: 'profile', to: '/profile', icon: User, label: 'Me' },
  ];

  return (
    <>
      <nav className="feed-bottom-nav fixed bottom-0 left-0 right-0 z-50 safe-area-bottom">
        <div className="flex items-center justify-around py-2 px-2">
          {navItems.map((item) => {
            const isActive = item.to === location.pathname;
            const Icon = item.icon;

            // Special "Create" center button — opens Add Item form
            if (item.id === 'create') {
              return (
                <button
                  key={item.id}
                  onClick={handleCreateClick}
                  className="flex items-center justify-center -mt-1"
                >
                  <div className="relative w-12 h-8 flex items-center justify-center rounded-lg overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary-400 via-primary-500 to-teal-400 rounded-lg" />
                    <PlusSquare className="w-5 h-5 text-white relative z-10" strokeWidth={2.5} />
                  </div>
                </button>
              );
            }

            // Search button opens overlay instead of navigating
            if (item.id === 'search') {
              return (
                <button
                  key={item.id}
                  onClick={onSearchOpen}
                  className="flex flex-col items-center justify-center gap-0.5 py-1 min-w-[48px]"
                >
                  <Icon className="w-[22px] h-[22px]" style={{ color: 'var(--nav-text-muted)' }} />
                  <span className="text-[10px] font-medium" style={{ color: 'var(--nav-text-muted)' }}>
                    {item.label}
                  </span>
                </button>
              );
            }

            return (
              <Link
                key={item.id}
                to={item.id === 'chat' && !user ? '/login' : item.to}
                className="flex flex-col items-center justify-center gap-0.5 py-1 min-w-[48px] relative"
              >
                <div className="relative">
                  <Icon
                    className={`w-[22px] h-[22px] transition-colors duration-200`}
                    style={{ color: isActive ? 'var(--nav-text)' : 'var(--nav-text-muted)' }}
                    strokeWidth={isActive ? 2.5 : 1.8}
                  />
                  {/* Unread badge for chat */}
                  {item.id === 'chat' && unreadCount > 0 && (
                    <div className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 bg-red-500 rounded-full flex items-center justify-center">
                      <span className="text-[9px] text-white font-bold">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    </div>
                  )}
                </div>
                <span
                  className={`text-[10px] font-medium transition-colors duration-200`}
                  style={{ color: isActive ? 'var(--nav-text)' : 'var(--nav-text-muted)' }}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Modals rendered outside <nav> to avoid backdrop-filter containing block issue */}
      {showSellerPrompt && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full relative">
            <button
              onClick={() => setShowSellerPrompt(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                <ShoppingCart className="w-7 h-7 text-primary-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {t('sellerProfileNeeded')}
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                {t('sellerProfileNeededDesc')}
              </p>
              <button
                onClick={handleOpenSellerForm}
                className="w-full py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
              >
                {t('createSellerProfile')}
              </button>
              <button
                onClick={() => setShowSellerPrompt(false)}
                className="mt-3 text-sm text-gray-500 hover:text-gray-700"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSellerForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <GoogleMapsProvider>
              <SellerProfileForm
                initialData={{}}
                onSubmit={handleSellerProfileSubmit}
                onCancel={() => setShowSellerForm(false)}
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
                pickupLocations={pickupLocations}
                setPickupLocations={setPickupLocations}
              />
            </GoogleMapsProvider>
          </div>
        </div>
      )}
    </>
  );
};
